import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import '../AdminPage.css';
import { vuser } from '../../services/api';
import type { Experiment, ExperimentScoreBreakdown, ExperimentTokenStatistics, Dialog, Reaction } from '../../types/vuser';
import {
  getDialogStepBodyDisplay,
  getPipelineStepDisplayName,
  isDialogReasoningRole,
} from '../../utils/vuserDialogDisplay';
import ProgressBar from '../ui/ProgressBar';
import { getExperimentProgressMetrics } from '../../utils/progressBar';
import { resolveExperimentDisplayState } from '../../utils/experimentDisplay';
import ExperimentStatusBadge from '../dataset/ExperimentStatusBadge';
import '../dataset/ExperimentStatusBadge.css';

function fmtInt(n: any): string {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString();
}

function fmtPct(n: any): string {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '-';
  return `${v.toFixed(1)}%`;
}

function fmtDate(s: any): string {
  if (!s) return '-';
  try {
    return new Date(String(s)).toLocaleString();
  } catch {
    return String(s);
  }
}

// Add CSS for message reactions
const messageReactionsStyle = document.createElement('style');
messageReactionsStyle.textContent = `
  .message__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    gap: 12px;
  }
  
  .message__reactions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  
  .reaction-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 700;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(10, 42, 71, 0.1);
    border: 2px solid transparent;
  }
  
  .reaction-badge:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(10, 42, 71, 0.15);
  }
  
  .reaction-badge--like {
    background: #0099FA;
    color: #FFFFFF;
    border-color: #0072C6;
  }
  
  .reaction-badge--dislike {
    background: #FF8300;
    color: #FFFFFF;
    border-color: #FF8300;
  }
  
  .reaction-badge i {
    font-size: 16px;
    line-height: 1;
  }
  
  .reaction-count {
    font-size: 14px;
    line-height: 1;
    font-weight: 700;
    min-width: 18px;
    text-align: center;
  }
  
  .reaction-loading {
    display: inline-flex;
    align-items: center;
    color: #7F9AB3;
    font-size: 12px;
  }
  
  .reaction-loading i {
    font-size: 11px;
  }

  .message--reasoning {
    background: rgba(147, 199, 242, 0.08);
    border-left: 3px solid #93C7F2;
  }

  .message--reasoning .message__role {
    color: #0099FA;
  }

  .message--system {
    background: #FFFFFF;
    border-left: 3px solid #7F9AB3;
  }
`;
if (!document.head.querySelector('style[data-message-reactions]')) {
  messageReactionsStyle.setAttribute('data-message-reactions', 'true');
  document.head.appendChild(messageReactionsStyle);
}

export interface ExperimentScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  experiment?: Experiment | null;
  score?: ExperimentScoreBreakdown | null;
  stats?: ExperimentTokenStatistics | null;
  dialogs?: Dialog[];
  integrationId?: string | null;
  loading?: boolean;
  error?: string | null;
}

const ExperimentScoreModal: React.FC<ExperimentScoreModalProps> = ({
  isOpen,
  onClose,
  experiment,
  score,
  stats,
  dialogs = [],
  integrationId,
  loading,
  error,
}) => {
  const [currentDialogIndex, setCurrentDialogIndex] = useState(0);
  const [dialogsWithMessages, setDialogsWithMessages] = useState<Dialog[]>(dialogs);
  const [loadingMessages, setLoadingMessages] = useState<string | null>(null);
  const loadingDialogIdRef = useRef<string | null>(null);
  const [expandedIntermediateMessages, setExpandedIntermediateMessages] = useState<Set<string>>(new Set());
  const [expandedAssistantSteps, setExpandedAssistantSteps] = useState<Set<string>>(new Set());
  const [messageReactions, setMessageReactions] = useState<Record<string, Reaction[]>>({});
  const [loadingReactions, setLoadingReactions] = useState<Set<string>>(new Set());

  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position (if not already saved by parent)
      const scrollY = window.scrollY;
      if (document.body.style.position !== 'fixed') {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      }
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        const savedScrollY = parseInt(scrollY || '0') * -1;
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollY);
        });
      }
    }
    return () => {
      // Cleanup on unmount only if modal is closed
      if (!isOpen) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  // Синхронизация с пропсами до paint: клонируем, чтобы не терять запрос на повторном открытии
  // (React может не применить setState, если ссылка на массив та же), и всегда сбрасываесем «обогащённые» messages.
  useLayoutEffect(() => {
    setDialogsWithMessages(dialogs.map((d) => ({ ...d })));
    setLoadingMessages(null);
    loadingDialogIdRef.current = null;
    setMessageReactions({});
    setExpandedIntermediateMessages(new Set());
    setExpandedAssistantSteps(new Set());
    if (isOpen && dialogs.length > 0) {
      setCurrentDialogIndex(0);
    }
  }, [dialogs, isOpen, experiment?.id]);

  // Убеждаемся, что currentDialogIndex валиден после обновления dialogsWithMessages
  useEffect(() => {
    if (isOpen && dialogsWithMessages.length > 0) {
      // Если индекс выходит за границы, сбрасываем на 0
      if (currentDialogIndex < 0 || currentDialogIndex >= dialogsWithMessages.length) {
        setCurrentDialogIndex(0);
      }
    }
  }, [dialogsWithMessages.length, isOpen, currentDialogIndex]);

  const currentDialog = useMemo(() => {
    if (!dialogsWithMessages.length || currentDialogIndex < 0 || currentDialogIndex >= dialogsWithMessages.length) return null;
    return dialogsWithMessages[currentDialogIndex];
  }, [dialogsWithMessages, currentDialogIndex]);

  const currentDialogId = currentDialog?.id;
  const currentMessagesCount = currentDialog?.messages?.length ?? 0;

  // Функция для загрузки реакций для сообщения
  const loadMessageReactions = useCallback(async (messageId: string) => {
    if (!integrationId) return;
    
    // Проверяем, не загружается ли уже
    setLoadingReactions((prev) => {
      if (prev.has(messageId)) return prev;
      return new Set(prev).add(messageId);
    });
    
    try {
      const resp = await vuser.reactionsByMessage(integrationId, messageId);
      const reactions = (resp as any)?.data ?? resp;
      if (Array.isArray(reactions)) {
        setMessageReactions((prev) => ({
          ...prev,
          [messageId]: reactions as Reaction[],
        }));
      }
    } catch (e: any) {
      console.warn(`Failed to load reactions for message ${messageId}:`, e?.message || e);
    } finally {
      setLoadingReactions((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  }, [integrationId]);

  // Загружаем сообщения диалога при выборе (cleanup отменяет гонки при закрытии модалки / смене диалога)
  useEffect(() => {
    if (!currentDialogId || !integrationId || !isOpen) return;
    if (currentMessagesCount > 0) return;

    loadingDialogIdRef.current = currentDialogId;
    setLoadingMessages(currentDialogId);

    let cancelled = false;

    void (async () => {
      try {
        const resp = await vuser.getDialog(integrationId, currentDialogId);
        if (cancelled) return;
        const dialogWithMessages = (resp as any)?.data ?? resp;

        setDialogsWithMessages((prev) =>
          prev.map((d) => (d.id === currentDialogId ? (dialogWithMessages as Dialog) : d))
        );
      } catch (e: any) {
        if (!cancelled) {
          console.warn('Failed to load dialog messages:', e?.message || e);
        }
      } finally {
        if (!cancelled) {
          loadingDialogIdRef.current = null;
          setLoadingMessages(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      loadingDialogIdRef.current = null;
      setLoadingMessages(null);
    };
  }, [currentDialogId, currentMessagesCount, integrationId, isOpen]);

  // Загружаем реакции для всех сообщений текущего диалога
  useEffect(() => {
    if (!currentDialog || !integrationId || !isOpen || !currentDialog.messages) return;
    
    currentDialog.messages.forEach((msg) => {
      if (msg.role !== 'assistant' && msg.role !== 'user') return;
      if (!messageReactions[msg.id]) {
        void loadMessageReactions(msg.id);
      }
    });
  }, [currentDialog?.id, currentDialog?.messages?.length, integrationId, isOpen, loadMessageReactions]);

  const handleSelectDialog = (index: number) => {
    if (index >= 0 && index < dialogsWithMessages.length) setCurrentDialogIndex(index);
  };

  // Функция для получения читаемого статуса
  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'completed': 'Completed',
      'in_progress': 'Running',
      'cancelled': 'Cancelled',
      'failed': 'Failed',
    };
    return statusMap[status] || status;
  };

  // Prevent scroll when modal opens
  useEffect(() => {
    if (isOpen) {
      // Ensure scroll position is maintained
      const currentScrollY = window.scrollY;
      // Force scroll to current position to prevent any automatic scrolling
      window.scrollTo(0, currentScrollY);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const progressMetrics = experiment ? getExperimentProgressMetrics(experiment) : null;
  const display = experiment ? resolveExperimentDisplayState(experiment) : null;
  const progressSubLabel = progressMetrics
    ? [progressMetrics.subLabel, display?.progressNote].filter(Boolean).join(' · ')
    : undefined;

  const scoreRows: Array<{ label: string; value: string }> = [
    { label: 'Likes (user)', value: fmtInt((score as any)?.likes_user) },
    { label: 'Dislikes (user)', value: fmtInt((score as any)?.dislikes_user) },
    { label: 'Likes (admin)', value: fmtInt((score as any)?.likes_admin) },
    { label: 'Dislikes (admin)', value: fmtInt((score as any)?.dislikes_admin) },
    { label: 'Comments', value: fmtInt((score as any)?.comment_count) },
    { label: 'References', value: fmtInt((score as any)?.reference_count) },
    { label: 'p_like_user', value: fmtPct((score as any)?.p_like_user_percent) },
    { label: 'p_dislike_user', value: fmtPct((score as any)?.p_dislike_user_percent) },
    { label: 'p_like_admin', value: fmtPct((score as any)?.p_like_admin_percent) },
    { label: 'p_dislike_admin', value: fmtPct((score as any)?.p_dislike_admin_percent) },
    { label: 'q_comment', value: fmtPct((score as any)?.q_comment_percent) },
    { label: 'q_reference', value: fmtPct((score as any)?.q_reference_percent) },
  ];

  const evalByReaction = (stats as any)?.evaluation_by_reaction || {};
  const evalRows = Object.keys(evalByReaction).length
    ? Object.entries(evalByReaction).map(([k, v]) => ({ k, v: fmtInt(v) }))
    : [];

  // Human-readable labels for evaluation_by_reaction keys
  const reactionKeyLabels: Record<string, string> = {
    like: 'Like — tokens used to evaluate assistant messages that received a like',
    dislike: 'Dislike — tokens used to evaluate assistant messages that received a dislike',
    comment: 'Comment — tokens used when the evaluator left a comment',
    reference_answer: 'Reference answer — tokens used for reference-answer evaluation',
    without_reaction:
      'No reaction — tokens spent on evaluation when evaluators chose not to assign a reaction',
    default: 'Other reaction types',
  };
  const getReactionLabel = (key: string) => reactionKeyLabels[key.toLowerCase()] || reactionKeyLabels.default || key;

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-content--wide">
        <div className="modal-header">
          <h3>Experiment score</h3>
          <button 
            className="modal-close" 
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="admin-message error" style={{ margin: 0, marginBottom: 16 }}>
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {loading ? (
            <div className="admin-loading" style={{ padding: 24 }}>
              <div className="spinner spinner--small"></div>
              <p>Loading…</p>
            </div>
          ) : (
            <>
              <div className="score-grid">
                <div className="score-card">
                  <div className="score-card__title">Overview</div>
                  <div className="kv-grid">
                    {experiment?.name && (
                      <div className="kv">
                        <div className="kv__k">Dataset</div>
                        <div className="kv__v" style={{ 
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={experiment.name}>
                          {experiment.name}
                        </div>
                      </div>
                    )}
                    <div className="kv">
                      <div className="kv__k">Experiment ID</div>
                      <div className="kv__v mono" style={{ 
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={experiment?.id || '-'}>
                        {experiment?.id || '-'}
                      </div>
                    </div>
                    <div className="kv">
                      <div className="kv__k">Status</div>
                      <div className="kv__v">
                        {experiment ? <ExperimentStatusBadge experiment={experiment} showWarningMessage /> : '—'}
                      </div>
                    </div>
                    {experiment?.dataset_status && display?.statusKind !== 'completed_warnings' ? (
                      <div className="kv">
                        <div className="kv__k">Dataset status</div>
                        <div className="kv__v">
                          <span className={`pill pill--${experiment.dataset_status}`}>{experiment.dataset_status}</span>
                        </div>
                      </div>
                    ) : null}
                    <div className="kv">
                      <div className="kv__k">Progress</div>
                      <div className="kv__v">
                        {progressMetrics && progressMetrics.total > 0 ? (
                          <ProgressBar
                            completed={progressMetrics.completed}
                            total={progressMetrics.total}
                            failed={progressMetrics.failed}
                            subLabel={progressSubLabel}
                            variant={display?.progressVariant ?? 'running'}
                            title={
                              progressMetrics.failed > 0
                                ? `${progressMetrics.failed} message(s) failed`
                                : display?.progressNote || undefined
                            }
                          />
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                    <div className="kv">
                      <div className="kv__k">Score</div>
                      <div className="kv__v">
                        {typeof experiment?.score === 'number' ? experiment.score.toFixed(4) : '-'}
                        {display?.scoreCaption ? (
                          <span className="experiment-score-caption">{display.scoreCaption}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="kv">
                      <div className="kv__k">Started</div>
                      <div className="kv__v">{fmtDate(experiment?.started_at)}</div>
                    </div>
                    <div className="kv">
                      <div className="kv__k">Finished</div>
                      <div className="kv__v">{fmtDate(experiment?.finished_at)}</div>
                    </div>
                  </div>
                </div>

                <div className="score-card">
                  <div className="score-card__title">Token statistics</div>
                  <p className="score-card__hint" style={{ margin: '0 0 12px 0', fontSize: 12, color: '#5B7893', lineHeight: 1.4 }}>
                    Token usage for this experiment: dataset size and total tokens consumed during scoring and evaluation.
                  </p>
                  <div className="kv-grid">
                    <div className="kv">
                      <div className="kv__k" title="Number of tokens in the dataset texts (user + assistant messages) used in this experiment">
                        Dataset text tokens
                      </div>
                      <div className="kv__v">{fmtInt((stats as any)?.dataset_text_tokens)}</div>
                    </div>
                    <div className="kv">
                      <div className="kv__k" title="Total tokens consumed by the system during scoring (API calls, evaluation, etc.)">
                        Total processing tokens
                      </div>
                      <div className="kv__v">{fmtInt((stats as any)?.total_processing_tokens)}</div>
                    </div>
                  </div>
                  {evalRows.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div className="score-subtitle">Evaluation by reaction</div>
                      <p className="score-card__hint" style={{ margin: '0 0 8px 0', fontSize: 11, color: '#5B7893', lineHeight: 1.4 }}>
                        Token usage broken down by type of evaluator reaction (like, dislike, comment, etc.). Each row shows how many tokens were used when evaluating messages that received that reaction.
                      </p>
                      <div className="mini-table">
                        {evalRows.map((r) => (
                          <div key={r.k} className="mini-table__row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                              <div className="mini-table__k" title={getReactionLabel(r.k)}>
                                {r.k.replace(/_/g, ' ')}
                              </div>
                              <div className="mini-table__v">{r.v}</div>
                            </div>
                            <div style={{ fontSize: 10, color: '#7F9AB3', lineHeight: 1.3 }}>
                              {getReactionLabel(r.k)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="score-card" style={{ marginTop: 16 }}>
                <div className="score-card__title">Score breakdown</div>
                <div className="score-breakdown">
                  {scoreRows.map((r) => (
                    <div key={r.label} className="score-item">
                      <div className="score-item__k">{r.label}</div>
                      <div className="score-item__v">{r.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dialogs section — список из dialogsWithMessages, чтобы совпадал с currentDialog */}
              {dialogsWithMessages.length > 0 && (
                <div className="dialogs-container" style={{ marginTop: 16 }}>
                  <div className="dialogs-list-column">
                    <div className="dialogs-list-header">
                      <div className="score-card__title">Dialogs ({dialogsWithMessages.length})</div>
                    </div>
                    <div className="dialogs-list">
                      {dialogsWithMessages.map((dialog, idx) => (
                        <div
                          key={dialog.id}
                          className={`dialog-item ${idx === currentDialogIndex ? 'dialog-item--active' : ''}`}
                          onClick={() => handleSelectDialog(idx)}
                        >
                          <div className="dialog-item__main">
                            <div className="dialog-item__number">Dialog {idx + 1}</div>
                            <div className="dialog-item__chat-id" title={dialog.id}>
                              Chat ID: <span className="mono">{dialog.id}</span>
                            </div>
                          </div>
                          <div className="dialog-item__status">
                            <span className={`dialog-status-badge dialog-status-badge--${dialog.status}`}>
                              {getStatusLabel(dialog.status)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="dialog-content-column">
                    {currentDialog ? (
                      <div className="dialog-messages">
                        <div className="dialog-header dialog-header--with-chat-id">
                          <div className="dialog-header__row">
                            <div className="dialog-header__info">
                              <span className="dialog-title">Dialog {currentDialogIndex + 1}</span>
                              <span className={`dialog-status dialog-status--${currentDialog.status}`}>
                                {getStatusLabel(currentDialog.status)}
                              </span>
                            </div>
                            <div className="dialog-header__meta">
                              <span className="dialog-date" title={currentDialog.id}>
                                {fmtDate(currentDialog.created_at)}
                              </span>
                            </div>
                          </div>
                          <div className="dialog-header__chat-id-block">
                            <span className="dialog-header__chat-id-label">Chat ID</span>
                            <code className="dialog-header__chat-id-value">{currentDialog.id}</code>
                          </div>
                        </div>
                        {loadingMessages === currentDialog.id ? (
                          <div className="admin-loading" style={{ padding: 24 }}>
                            <div className="spinner spinner--small"></div>
                            <p>Loading messages…</p>
                          </div>
                        ) : currentDialog.messages && currentDialog.messages.length > 0 ? (
                          <div className="messages-list">
                            {currentDialog.messages
                              .sort((a, b) => {
                                // Сортируем по created_at (хронологический порядок)
                                const dateA = new Date(a.created_at).getTime();
                                const dateB = new Date(b.created_at).getTime();
                                if (dateA !== dateB) {
                                  return dateA - dateB; // По возрастанию (старые сообщения первыми)
                                }
                                // Если даты одинаковые, используем sequence как дополнительный критерий
                                return a.sequence - b.sequence;
                              })
                              .map((msg) => {
                                if (isDialogReasoningRole(msg.role)) {
                                  return (
                                    <div key={msg.id} className="message message--reasoning">
                                      <div className="message__header">
                                        <div className="message__role">🧠 Reasoning</div>
                                      </div>
                                      <div className="message__content">{msg.content}</div>
                                    </div>
                                  );
                                }
                                if (msg.role !== 'user' && msg.role !== 'assistant') {
                                  return (
                                    <div key={msg.id} className="message message--system">
                                      <div className="message__header">
                                        <div className="message__role">{msg.role || 'Message'}</div>
                                      </div>
                                      <div className="message__content">{msg.content}</div>
                                    </div>
                                  );
                                }

                                const hasIntermediateMessages =
                                  msg.role === 'assistant' &&
                                  msg.intermediate_messages &&
                                  msg.intermediate_messages.length > 0;
                                const hasPipelineSteps = Array.isArray(msg.steps) && msg.steps.length > 0;
                                const isExpanded = expandedIntermediateMessages.has(msg.id);
                                const stepsExpanded = expandedAssistantSteps.has(msg.id);
                                const reactions = messageReactions[msg.id] || [];
                                const likes = reactions.filter((r) => r.type === 'like').length;
                                const dislikes = reactions.filter((r) => r.type === 'dislike').length;
                                const isLoadingReactions = loadingReactions.has(msg.id);

                                return (
                                  <div key={msg.id} className={`message message--${msg.role}`}>
                                    <div className="message__header">
                                      <div className="message__role">
                                        {msg.role === 'user' ? '👤 User' : '🤖 Assistant'}
                                      </div>
                                      {msg.role === 'assistant' && (
                                        <div className="message__reactions">
                                          {isLoadingReactions ? (
                                            <span className="reaction-loading">
                                              <i className="fas fa-spinner fa-spin"></i>
                                            </span>
                                          ) : (
                                            <>
                                              {likes > 0 && (
                                                <span
                                                  className="reaction-badge reaction-badge--like"
                                                  title={`${likes} like${likes !== 1 ? 's' : ''}`}
                                                >
                                                  <i className="fas fa-thumbs-up"></i>
                                                  <span className="reaction-count">{likes}</span>
                                                </span>
                                              )}
                                              {dislikes > 0 && (
                                                <span
                                                  className="reaction-badge reaction-badge--dislike"
                                                  title={`${dislikes} dislike${dislikes !== 1 ? 's' : ''}`}
                                                >
                                                  <i className="fas fa-thumbs-down"></i>
                                                  <span className="reaction-count">{dislikes}</span>
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="message__content">{msg.content}</div>
                                    {hasPipelineSteps && (
                                      <div className="intermediate-messages-container">
                                        <button
                                          type="button"
                                          className="intermediate-toggle-btn"
                                          onClick={() => {
                                            setExpandedAssistantSteps((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(msg.id)) next.delete(msg.id);
                                              else next.add(msg.id);
                                              return next;
                                            });
                                          }}
                                        >
                                          <i className={`fas fa-${stepsExpanded ? 'eye-slash' : 'eye'}`}></i>
                                          {stepsExpanded ? 'Hide' : 'Show'} reasoning ({msg.steps!.length})
                                        </button>
                                        {stepsExpanded && msg.steps && (
                                          <div className="intermediate-messages expanded">
                                            <div className="intermediate-header">
                                              <i className="fas fa-layer-group"></i>
                                              <span>Reasoning</span>
                                            </div>
                                            <div className="intermediate-content">
                                              {msg.steps.map((step, idx) => (
                                                <div
                                                  key={String((step as { id?: string }).id ?? idx)}
                                                  className="intermediate-message"
                                                >
                                                  <div className="intermediate-agent-name">
                                                    <i className="fas fa-robot"></i>
                                                    {getPipelineStepDisplayName(step, idx)}
                                                  </div>
                                                  <div className="intermediate-message-content">
                                                    {getDialogStepBodyDisplay(step)}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {hasIntermediateMessages && (
                                      <div className="intermediate-messages-container">
                                        <button
                                          type="button"
                                          className="intermediate-toggle-btn"
                                          onClick={() => {
                                            setExpandedIntermediateMessages((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(msg.id)) {
                                                next.delete(msg.id);
                                              } else {
                                                next.add(msg.id);
                                              }
                                              return next;
                                            });
                                          }}
                                        >
                                          <i className={`fas fa-${isExpanded ? 'eye-slash' : 'eye'}`}></i>
                                          {isExpanded ? 'Hide' : 'Show'} Reasoning Process (legacy)
                                        </button>
                                        {isExpanded && msg.intermediate_messages && (
                                          <div className="intermediate-messages expanded">
                                            <div className="intermediate-header">
                                              <i className="fas fa-cogs"></i>
                                              <span>Agent Reasoning Process</span>
                                            </div>
                                            <div className="intermediate-content">
                                              {msg.intermediate_messages.map((intermediateMsg, idx) => (
                                                <div key={idx} className="intermediate-message">
                                                  <div className="intermediate-agent-name">
                                                    <i className="fas fa-robot"></i>
                                                    {intermediateMsg.agent_name || `Agent ${idx + 1}`}
                                                  </div>
                                                  <div className="intermediate-message-content">
                                                    {typeof intermediateMsg.content === 'string'
                                                      ? intermediateMsg.content
                                                      : JSON.stringify(intermediateMsg.content, null, 2)}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <div className="empty-state" style={{ marginTop: 12 }}>
                            <div className="empty-title">No messages</div>
                            <div className="empty-subtitle">This dialog has no messages yet.</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="empty-state" style={{ padding: 24 }}>
                        <div className="empty-title">Select a dialog</div>
                        <div className="empty-subtitle">Select a dialog from the list on the left to view.</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExperimentScoreModal;
